import { RequireOnly } from '@pelicargo/types';
import { DimensionUnit, LineItem, LineItemType, WeightUnit } from '@prisma/client';
import { isNil } from 'lodash';

import { fixNumTo, modelUtils } from '.';
import { Units } from './constants';

export type CalculateOptions = {
  ceil?: boolean;
  fixTo?: number;
};

const defaultOptions: CalculateOptions = { ceil: false, fixTo: undefined };

// Volumetric Weight

type CargoParams = {
  length: number;
  width: number;
  height: number;
  quantity: number;
  weight?: number;
};

export const getVolumetricWeight = ({ length, width, height, quantity }: CargoParams, options?: CalculateOptions) => {
  const { ceil, fixTo } = { ...defaultOptions, ...options };
  const volume = length * width * height;
  const volumetricWeight = volume / 6000;
  const totalVolumetricWeight = volumetricWeight * quantity;

  if (ceil) return Math.ceil(totalVolumetricWeight);
  // fixTo: 0 means round to integers, so check presence rather than truthiness
  if (!isNil(fixTo)) return fixNumTo(totalVolumetricWeight, fixTo);

  return totalVolumetricWeight;
};

export const getVolumeCBM = ({ length, width, height, quantity }: CargoParams, options?: CalculateOptions) => {
  const { ceil, fixTo } = { ...defaultOptions, ...options };

  const volumeInCm = length * width * height * quantity;
  const volumeInM = volumeInCm * 1e-6;

  if (ceil) return Math.ceil(volumeInM);
  // fixTo: 0 means round to integers, so check presence rather than truthiness
  if (!isNil(fixTo)) return fixNumTo(volumeInM, fixTo);

  return volumeInM;
};

// Gross Weight

type GrossWeightParams = CargoParams & { weight?: number };

export const getGrossWeight = ({ quantity, weight }: GrossWeightParams, options?: CalculateOptions) => {
  const { ceil, fixTo } = { ...defaultOptions, ...options };

  const grossWeight = quantity * (weight || 0);

  if (ceil) return Math.ceil(grossWeight);
  // fixTo: 0 means round to integers, so check presence rather than truthiness
  if (!isNil(fixTo)) return fixNumTo(grossWeight, fixTo);

  return grossWeight;
};

export const getDerivedWeight = (
  { quantity, length, width, height }: CargoParams,
  { gw, totalVw }: { gw: number; totalVw: number },
  options?: CalculateOptions,
) => {
  const { ceil, fixTo } = { ...defaultOptions, ...options };

  const vw = getVolumetricWeight({ length, width, height, quantity });
  const shareOfVw = vw / totalVw;
  const derivedWeight = (gw * shareOfVw) / quantity;

  if (ceil) return Math.ceil(derivedWeight);
  // fixTo: 0 means round to integers, so check presence rather than truthiness
  if (!isNil(fixTo)) return fixNumTo(derivedWeight, fixTo);

  return derivedWeight;
};

export const lbToKg = (lb: number) => lb * 0.45359237;
export const kgToLb = (kg: number) => kg * 2.20462262185;
export const cmToIn = (cm: number) => cm * 0.393700787;
export const inToCm = (inch: number) => inch * 2.54;
export const cmToFt = (cm: number) => cm * 0.0328084;
export const miToKm = (mile: number) => mile * 1.609344;
export const kmToMi = (km: number) => km * 0.621371192;

export const ccToM3 = (cc: number) => cc / 1000000;
export const cfToM3 = (cf: number) => cf / 35.31469989;
export const ciToM3 = (ci: number) => ci / 61012.81269067;

export const ensureMetricCargo = (cargo: CargoParams, { weightUnit, dimensionUnit }: Units) => {
  const { weight, length, width, height } = cargo;

  const nextCargo = { ...cargo };

  if (weight) {
    const isKg = weightUnit === WeightUnit.kg;
    if (!isKg) nextCargo.weight = lbToKg(weight);
  }

  const isCm = dimensionUnit === DimensionUnit.cm;
  if (!isCm) {
    nextCargo.length = inToCm(length);
    nextCargo.width = inToCm(width);
    nextCargo.height = inToCm(height);
  }

  // If we originally had the cargo weight as null, we want to keep it null instead of NaN
  if (cargo?.weight === null) nextCargo.weight = undefined;

  return nextCargo;
};

export const effectiveRate = (
  lineItems: RequireOnly<LineItem, 'per_unit_count' | 'cost' | 'per_unit_cost' | 'type'>[],
  cw: number,
) => {
  const airFreight = lineItems?.find((item) => modelUtils.lineItem.isRateCategory(item.type));
  const cost = airFreight?.cost || 0;

  if (!airFreight?.per_unit_cost) return cost / cw;

  return airFreight.per_unit_cost;
};

export const totalCost = (lineItems: RequireOnly<LineItem, 'cost'>[]) => {
  return lineItems?.reduce((acc, item) => {
    const cost = item.cost || 0;

    return acc + cost;
  }, 0);
};

const EXCLUSIVE_VARIABLE_RATE_LINE_ITEM_TYPES: LineItemType[] = [
  LineItemType.TARIFF,
  LineItemType.CONTRACT,
  LineItemType.PROMO,
  LineItemType.SPOT,
  LineItemType.DISRUPTION,
  LineItemType.FUEL,
  LineItemType.SECURITY,
  LineItemType.TRUCKING,
  LineItemType.NON_STACKABLE,
  LineItemType.UNKNOWN_SHIPPER,
  LineItemType.FULFILLMENT_PARTNER_MARKUP,
  LineItemType.SYSTEM_MARKUP,
  LineItemType.TENANT_MARKUP,
  LineItemType.OTHER_MARKUP,
  LineItemType.OTHER,
  LineItemType.PICK_UP,
  LineItemType.AWB_LABELING,
  LineItemType.TRANSFER,
  LineItemType.HAZARDOUS,
  LineItemType.DOCUMENT_PRINTING,
];

export const exclusiveVariableRate = (lineItems: RequireOnly<LineItem, 'type' | 'cost'>[], cw: number) => {
  const lineItemsToInclude = lineItems?.filter((item) => {
    return EXCLUSIVE_VARIABLE_RATE_LINE_ITEM_TYPES.includes(item.type as LineItemType);
  });

  const cost = totalCost(lineItemsToInclude);
  return cost / cw;
};

const ALL_IN_VARIABLE_RATE_LINE_ITEM_TYPES: LineItemType[] = [
  ...EXCLUSIVE_VARIABLE_RATE_LINE_ITEM_TYPES,
  LineItemType.SCREENING,
];

export const allInVariableRate = (lineItems: RequireOnly<LineItem, 'type' | 'cost'>[], cw: number) => {
  const lineItemsToInclude = lineItems?.filter((item) => {
    return ALL_IN_VARIABLE_RATE_LINE_ITEM_TYPES.includes(item.type as LineItemType);
  });

  const cost = totalCost(lineItemsToInclude);
  return cost / cw;
};

export const screeningFee = (lineItems: RequireOnly<LineItem, 'cost' | 'type'>[]) => {
  const screening = lineItems?.find((item) => item.type === LineItemType.SCREENING);

  return screening?.per_unit_cost || screening?.cost;
};

export const getTotalRate = (
  lineItems: RequireOnly<LineItem, 'cost' | 'type' | 'per_unit_cost' | 'is_per_unit' | 'per_unit_cost_type'>[],
) => {
  return lineItems?.reduce((agg, li) => {
    if (modelUtils.lineItem.isRateCategory(li.type) || li.is_per_unit) {
      const lineItemRate = li.cost / (li.per_unit_count || 0);
      return agg + lineItemRate;
    }
    return agg;
  }, 0);
};

export const getOtherFeesTotal = (
  lineItems: RequireOnly<LineItem, 'cost' | 'type' | 'per_unit_cost' | 'is_per_unit' | 'per_unit_cost_type' | 'type'>[],
) => {
  return lineItems
    ?.filter((li) => !EXCLUSIVE_VARIABLE_RATE_LINE_ITEM_TYPES.includes(li.type) && li.type !== LineItemType.SCREENING)
    ?.reduce((agg, li) => {
      return agg + li.cost;
    }, 0);
};

type LatLng = {
  latitude: number;
  longitude: number;
};

export type AirportDistanceMi = {
  id: string;
  distanceMi: number;
};

export const getNearestAirportDistancesMi = (
  pickup: LatLng,
  airports: { id: string; latlng: number[] }[],
  limit = 3,
): AirportDistanceMi[] => {
  if (!Number.isFinite(pickup.latitude) || !Number.isFinite(pickup.longitude)) return [];

  return airports
    .filter((airport) => {
      const [latitude, longitude] = airport.latlng ?? [];
      return Number.isFinite(latitude) && Number.isFinite(longitude);
    })
    .map((airport) => ({
      id: airport.id,
      distanceMi: kmToMi(calculateDistance(pickup, { latitude: airport.latlng[0], longitude: airport.latlng[1] })),
    }))
    .filter(({ distanceMi }) => Number.isFinite(distanceMi))
    .sort((a, b) => a.distanceMi - b.distanceMi)
    .slice(0, limit);
};

/**
 * Converts degrees to radians.
 * @param degrees - The angle in degrees.
 * @returns The angle in radians.
 */
const toRadians = (degrees: number): number => {
  return degrees * (Math.PI / 180);
};

/**
 * Calculates the distance between two geographical points.
 * @param point1 - The first point.
 * @param point2 - The second point.
 * @returns The distance between the two points in kilometers.
 */
export const calculateDistance = (point1: LatLng, point2: LatLng) => {
  const R = 6371; // Earth's radius in kilometers

  const lat1Rad = toRadians(point1.latitude);
  const lon1Rad = toRadians(point1.longitude);
  const lat2Rad = toRadians(point2.latitude);
  const lon2Rad = toRadians(point2.longitude);

  const latSin = Math.sin((lat2Rad - lat1Rad) / 2);
  const lonSin = Math.sin((lon2Rad - lon1Rad) / 2);

  const a = latSin * latSin + Math.cos(lat1Rad) * Math.cos(lat2Rad) * lonSin * lonSin;

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  const distance = R * c; // Distance in kilometers

  return distance;
};
